'use client';

/**
 * useI18n Hook
 *
 * React hook for internationalization across OpsMatrix portals.
 * Provides type-safe translations and locale-aware formatting.
 *
 * @example
 * ```tsx
 * import { useI18n } from '@ops-matrix/design-system-ui';
 *
 * function SaveButton() {
 *   const { t } = useI18n();
 *   return <button>{t('common.save')}</button>;
 * }
 *
 * function PriceDisplay({ amount }: { amount: number }) {
 *   const { formatCurrency } = useI18n();
 *   return <span>{formatCurrency(amount)}</span>;
 * }
 * ```
 */

import {
  createTranslator,
  formatCurrency as formatCurrencyUtil,
  formatDateTime as formatDateTimeUtil,
  formatDate as formatDateUtil,
  formatNumber as formatNumberUtil,
  formatRelativeTime as formatRelativeTimeUtil,
  hasTranslation as hasTranslationUtil,
  LanguageCode,
  type TFunction,
} from '@ops-matrix/shared-types';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

// ============================================================================
// TYPES
// ============================================================================

export interface I18nContextValue {
  /** Current language code */
  language: LanguageCode;
  /** Change language */
  setLanguage: (lang: LanguageCode) => void;
  /** Translation function */
  t: TFunction;
  /** Check if translation exists */
  hasTranslation: (key: string) => boolean;
  /** Format date according to locale */
  formatDate: (date: Date | string, format?: 'short' | 'medium' | 'long') => string;
  /** Format date and time */
  formatDateTime: (date: Date | string) => string;
  /** Format number according to locale */
  formatNumber: (num: number, options?: Intl.NumberFormatOptions) => string;
  /** Format currency according to locale */
  formatCurrency: (amount: number, currency?: string) => string;
  /** Format relative time */
  formatRelativeTime: (date: Date | string) => string;
}

// ============================================================================
// CONTEXT
// ============================================================================

const I18nContext = createContext<I18nContextValue | null>(null);

// ============================================================================
// DEFAULT CONTEXT (memoized at module level to prevent infinite loops)
// ============================================================================

const DEFAULT_LANG = LanguageCode.EN;
const defaultTranslator = createTranslator(DEFAULT_LANG);

/**
 * Default context value used when no I18nProvider is present.
 * Memoized at module level to ensure stable references and prevent
 * infinite render loops when `t` is used in useEffect dependencies.
 */
const DEFAULT_CONTEXT: I18nContextValue = {
  language: DEFAULT_LANG,
  setLanguage: () => {
    console.warn('useI18n: setLanguage called without I18nProvider');
  },
  t: defaultTranslator,
  hasTranslation: (key: string) => hasTranslationUtil(key, DEFAULT_LANG),
  formatDate: (date, format) => formatDateUtil(date, DEFAULT_LANG, format),
  formatDateTime: (date) => formatDateTimeUtil(date, DEFAULT_LANG),
  formatNumber: (num, options) => formatNumberUtil(num, DEFAULT_LANG, options),
  formatCurrency: (amount, currency) => formatCurrencyUtil(amount, DEFAULT_LANG, currency),
  formatRelativeTime: (date) => formatRelativeTimeUtil(date, DEFAULT_LANG),
};

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to access i18n utilities
 *
 * Must be used within I18nProvider (or DesignSystemProvider which includes it).
 * Falls back to module-level default context when no provider is present
 * (stable reference prevents infinite loops when `t` is used in useEffect deps).
 */
export function useI18n(): I18nContextValue {
  return useContext(I18nContext) ?? DEFAULT_CONTEXT;
}

// ============================================================================
// PROVIDER VALUE HOOK
// ============================================================================

/**
 * Hook to create I18n context value
 * Used internally by I18nProvider
 */
export function useI18nValue(initialLanguage: LanguageCode = LanguageCode.EN): I18nContextValue {
  const [language, setLanguage] = useState<LanguageCode>(initialLanguage);

  // Create translator for current language
  const translator = useMemo(() => createTranslator(language), [language]);

  // Memoized formatting functions
  const formatDate = useCallback(
    (date: Date | string, format?: 'short' | 'medium' | 'long') => formatDateUtil(date, language, format),
    [language],
  );

  const formatDateTime = useCallback((date: Date | string) => formatDateTimeUtil(date, language), [language]);

  const formatNumber = useCallback(
    (num: number, options?: Intl.NumberFormatOptions) => formatNumberUtil(num, language, options),
    [language],
  );

  const formatCurrency = useCallback(
    (amount: number, currency?: string) => formatCurrencyUtil(amount, language, currency),
    [language],
  );

  const formatRelativeTime = useCallback((date: Date | string) => formatRelativeTimeUtil(date, language), [language]);

  const hasTranslation = useCallback((key: string) => hasTranslationUtil(key, language), [language]);

  return useMemo(
    () => ({
      language,
      setLanguage,
      t: translator,
      hasTranslation,
      formatDate,
      formatDateTime,
      formatNumber,
      formatCurrency,
      formatRelativeTime,
    }),
    [
      language,
      translator,
      hasTranslation,
      formatDate,
      formatDateTime,
      formatNumber,
      formatCurrency,
      formatRelativeTime,
    ],
  );
}

// ============================================================================
// PROVIDER
// ============================================================================

export interface I18nProviderProps {
  /** Children components */
  children: React.ReactNode;
  /** Initial language code */
  initialLanguage?: LanguageCode;
}

/**
 * I18n Provider that provides internationalization context to children.
 *
 * @example
 * ```tsx
 * <I18nProvider initialLanguage={LanguageCode.EN}>
 *   <App />
 * </I18nProvider>
 * ```
 */
export const I18nProvider: React.FC<I18nProviderProps> = ({ children, initialLanguage = LanguageCode.EN }) => {
  const value = useI18nValue(initialLanguage);

  return React.createElement(I18nContext.Provider, { value }, children);
};

// ============================================================================
// EXPORTS
// ============================================================================

/**
 * Translation helper for use outside React (e.g., in utils)
 */
export { createTranslator } from '@ops-matrix/shared-types';
export { I18nContext };
